import { ReactNode, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import {
    Button,
    Group,
    List,
    LoadingOverlay,
    Modal,
    Text,
    Title,
} from "@mantine/core";
import { TFunction } from "i18next";
import { get } from "lodash";
import { Review, ReviewDestination } from "src/graphql/generated/graphql";
import { useApproveReview } from "src/hooks/reviews";
import { handleError } from "src/utils/error";

import { CampaignQueryDataType, useCampaignProductReviewDestinations } from "../../hooks/campaigns";
import { createModal } from "../../hooks/modals";

export const validateConfig = (t: TFunction, reviewDestination: ReviewDestination): ReactNode => {
    const config = /^\{.*\}$/.test(reviewDestination?.config) ?
        JSON.parse(reviewDestination.config) : null;

    let error;

    for (const field of reviewDestination?.configFields || []) {
        if (field?.isRequiredForApproval) {
            if (!get(config, field.path)) {
                error = (
                    <>
                        {t("review_approve_error_missingfield", { name: reviewDestination?.name })}
                        <Text weight={500}>
                            {field.label}
                        </Text>
                    </>
                );
            }
        }
    }

    return error;
};

export interface ApproveReviewModalProps {
    review: Review;
    campaign?: CampaignQueryDataType;
    onApprove?();
    onClose?();
}

export const ApproveReviewModal = createModal(({ review, campaign, onApprove, onClose }: ApproveReviewModalProps) => {
    const { t } = useTranslation(["common"]);

    const {
        productReviewDestinations: remoteProductReviewDestinations,
        isLoadingProductReviewDestinations,
    } = useCampaignProductReviewDestinations(campaign?.id ? null : review?.campaign?.id);
    const [errors, setErrors] = useState<ReactNode[]>([]);

    const productReviewDestinations = campaign?.id ?
        campaign?.productReviewDestinations : remoteProductReviewDestinations;

    const { approveReview } = useApproveReview(review?.id);
    const handleApprove = useCallback(async () => {
        const productReviewDestination = (productReviewDestinations || [])
            .find(productReviewDestination => productReviewDestination?.productId === review?.product?.id);

        const errors: ReactNode[] = [];

        for (const contents of review?.contents || []) {
            const destinationId = [contents?.destination?.type, contents?.destination?.key].join("/");

            const destination = (productReviewDestination?.destinations || [])
                .find(destination => [destination?.type, destination?.key].join("/") === destinationId);

            const error = validateConfig(t, destination);

            if (error) {
                errors.push(error);
            }
        }

        if (errors?.length) {
            setErrors(errors);
            return;
        }

        try {
            await approveReview();
            onApprove?.();
            onClose?.();
        } catch (err) {
            handleError(err, { context: { reviewId: review?.id } });
        }
    }, [approveReview, onApprove, onClose, productReviewDestinations, review?.contents, review?.id, review?.product?.id, t]);

    return (
        <Modal
            size="md"
            styles={{
                content: {
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    overflowY: "visible !important" as any,
                },
                header: {
                    zIndex: 299,
                },
            }}
            title={
                <Title order={3}>
                    {errors?.length ? t("review_approve_error_title") : t("review_approve_confirm_title")}
                </Title>
            }
            onClose={onClose}
            opened
        >
            <LoadingOverlay visible={isLoadingProductReviewDestinations} />
            <Text>
                {errors?.length ? t("review_approve_error") : t("review_approve_confirm")}
            </Text>
            {!!errors?.length && (
                <List>
                    {errors.map((error, index) => (
                        <List.Item key={`${index}`}>
                            {error}
                        </List.Item>
                    ))}
                </List>
            )}
            <Group mt="md" spacing="md" position="right">
                {errors?.length ? (
                    <Button onClick={onClose}>
                        {t("yeahigetit")}
                    </Button>
                ) : (
                    <>
                        <Button
                            variant="subtle"
                            onClick={onClose}
                        >
                            {t("no")}
                        </Button>
                        <Button onClick={handleApprove}>
                            {t("yes")}
                        </Button>
                    </>
                )}
            </Group>
        </Modal>
    );
});
